import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  foundationCertificationApi,
  type CertificationDashboard,
  type CertificationReport,
  type CertificationStatus,
  type FoundationModule,
  type QualityScores,
  type Recommendation,
  type RiskItem,
  type TechnicalDebtItem,
} from '../../api/foundation-certification';

type Tab = 'overview' | 'modules' | 'scores' | 'risks' | 'debt' | 'recommendations' | 'status';
type Theme = 'light' | 'dark';

function statusColor(status: string, dark: boolean) {
  if (status === 'passed' || status === 'approved') return dark ? 'text-emerald-400' : 'text-emerald-600';
  if (status === 'warning' || status === 'approved_with_recommendations') return dark ? 'text-amber-400' : 'text-amber-600';
  if (status === 'failed' || status === 'rejected') return dark ? 'text-red-400' : 'text-red-600';
  return dark ? 'text-slate-400' : 'text-slate-500';
}

function levelBadge(level: string, dark: boolean) {
  const base = 'rounded-full px-3 py-1 text-xs font-semibold uppercase';
  if (level.includes('plus')) return `${base} ${dark ? 'bg-violet-900 text-violet-300' : 'bg-violet-100 text-violet-800'}`;
  if (level.includes('enterprise')) return `${base} ${dark ? 'bg-indigo-900 text-indigo-300' : 'bg-indigo-100 text-indigo-800'}`;
  return `${base} ${dark ? 'bg-sky-900 text-sky-300' : 'bg-sky-100 text-sky-800'}`;
}

export function FoundationCertificationPage() {
  const [theme, setTheme] = useState<Theme>('light');
  const dark = theme === 'dark';
  const [tab, setTab] = useState<Tab>('overview');
  const [dashboard, setDashboard] = useState<CertificationDashboard | null>(null);
  const [report, setReport] = useState<CertificationReport | null>(null);
  const [scores, setScores] = useState<QualityScores | null>(null);
  const [modules, setModules] = useState<FoundationModule[]>([]);
  const [risks, setRisks] = useState<RiskItem[]>([]);
  const [debt, setDebt] = useState<TechnicalDebtItem[]>([]);
  const [recommendations, setRecommendations] = useState<Recommendation[]>([]);
  const [status, setStatus] = useState<CertificationStatus | null>(null);
  const [actionMsg, setActionMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [dash, rep, sc, mods, rsk, dbt, recs, st] = await Promise.all([
        foundationCertificationApi.dashboard(),
        foundationCertificationApi.report(),
        foundationCertificationApi.scores(),
        foundationCertificationApi.modules(),
        foundationCertificationApi.risks(),
        foundationCertificationApi.debt(),
        foundationCertificationApi.recommendations(),
        foundationCertificationApi.status(),
      ]);
      setDashboard(dash);
      setReport(rep);
      setScores(sc);
      setModules(mods);
      setRisks(rsk);
      setDebt(dbt);
      setRecommendations(recs);
      setStatus(st);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load certification data');
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const runCertification = async () => {
    setBusy(true);
    setActionMsg(null);
    try {
      const result = await foundationCertificationApi.run();
      setActionMsg(`Certification complete: ${result.decision} (${result.level}) — ${result.go_no_go}`);
      refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Certification run failed');
    } finally {
      setBusy(false);
    }
  };

  const card = dark ? 'border-slate-800 bg-slate-900' : 'border-slate-200 bg-white';
  const muted = dark ? 'text-slate-400' : 'text-slate-500';
  const shell = dark ? 'bg-slate-950 text-slate-100' : 'bg-white text-slate-900';

  const tabs: { id: Tab; label: string }[] = [
    { id: 'overview', label: 'Executive Summary' },
    { id: 'modules', label: 'Module Audit' },
    { id: 'scores', label: 'Quality Scores' },
    { id: 'risks', label: 'Risk Register' },
    { id: 'debt', label: 'Technical Debt' },
    { id: 'recommendations', label: 'Recommendations' },
    { id: 'status', label: 'Certification Status' },
  ];

  const scoreEntries = scores ? Object.entries(scores).filter(([k]) => k !== 'overall_grade') : [];

  return (
    <div className={`space-y-6 rounded-xl p-1 ${shell}`}>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-violet-500">PRECHECK-000006</p>
          <h1 className="text-2xl font-semibold">Enterprise Foundation Platform V2.0 Certification</h1>
          <p className={`text-sm ${muted}`}>Architecture audit, cross-platform validation, production readiness & enterprise foundation approval</p>
          <div className="mt-2 flex flex-wrap gap-2 text-xs">
            <Link to="/platform/observability-governance" className="text-violet-500 hover:underline">Observability</Link>
            <span className={muted}>·</span>
            <Link to="/platform/commercial-governance" className="text-violet-500 hover:underline">Commercial</Link>
            <span className={muted}>·</span>
            <Link to="/command" className="text-violet-500 hover:underline">Command Center</Link>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => setTheme(dark ? 'light' : 'dark')} className={`rounded-lg border px-3 py-2 text-sm ${card}`} aria-label="Toggle dark mode">{dark ? 'Light' : 'Dark'}</button>
          <button type="button" onClick={runCertification} disabled={busy} className="rounded-lg bg-violet-600 px-4 py-2 text-sm font-medium text-white hover:bg-violet-700 disabled:opacity-50">Run Certification</button>
          <button type="button" onClick={refresh} disabled={busy} className={`rounded-lg border px-4 py-2 text-sm ${card} disabled:opacity-50`}>Refresh</button>
        </div>
      </div>

      {error && <div className={`rounded-lg border px-4 py-3 text-sm ${dark ? 'border-red-900 bg-red-950 text-red-300' : 'border-red-200 bg-red-50 text-red-700'}`}>{error}</div>}
      {actionMsg && <div className={`rounded-lg border px-4 py-3 text-sm ${dark ? 'border-emerald-900 bg-emerald-950 text-emerald-300' : 'border-emerald-200 bg-emerald-50 text-emerald-800'}`}>{actionMsg}</div>}

      {status && (
        <div className={`flex flex-wrap items-center gap-4 rounded-xl border p-4 ${card}`}>
          <span className={levelBadge(status.level, dark)}>{status.level.replace(/_/g, ' ')}</span>
          <span className={`text-lg font-semibold ${statusColor(status.decision, dark)}`}>{status.go_no_go}</span>
          <span className={`text-sm ${muted}`}>Grade: <strong>{status.overall_grade.toFixed(1)}</strong></span>
          {status.production_ready && <span className="rounded bg-emerald-500/20 px-2 py-0.5 text-xs text-emerald-500">Production Ready</span>}
          {status.feature_frozen && <span className="rounded bg-indigo-500/20 px-2 py-0.5 text-xs text-indigo-500">Feature Frozen</span>}
        </div>
      )}

      <div className="flex flex-wrap gap-2 border-b pb-2" role="tablist">
        {tabs.map((t) => (
          <button key={t.id} type="button" role="tab" aria-selected={tab === t.id} onClick={() => setTab(t.id)}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium ${tab === t.id ? 'bg-violet-600 text-white' : dark ? 'text-slate-400 hover:bg-slate-800' : 'text-slate-600 hover:bg-slate-100'}`}>{t.label}</button>
        ))}
      </div>

      {tab === 'overview' && report && dashboard && (
        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div className={`rounded-xl border p-4 ${card}`}><p className={`text-xs uppercase ${muted}`}>Overall Grade</p><p className="mt-1 text-3xl font-semibold text-violet-500">{report.scores.overall_grade.toFixed(1)}</p></div>
            <div className={`rounded-xl border p-4 ${card}`}><p className={`text-xs uppercase ${muted}`}>Modules Passed</p><p className="mt-1 text-3xl font-semibold">{dashboard.modules_passed}/{dashboard.modules_total}</p></div>
            <div className={`rounded-xl border p-4 ${card}`}><p className={`text-xs uppercase ${muted}`}>Critical Risks</p><p className="mt-1 text-3xl font-semibold text-amber-500">{dashboard.critical_risks}</p></div>
            <div className={`rounded-xl border p-4 ${card}`}><p className={`text-xs uppercase ${muted}`}>Open Debt Items</p><p className="mt-1 text-3xl font-semibold">{dashboard.open_debt_items}</p></div>
          </div>
          <div className={`rounded-xl border p-4 ${card}`}>
            <h2 className="font-medium">Executive Summary</h2>
            <p className={`mt-2 text-sm leading-relaxed ${muted}`}>{report.executive_summary}</p>
          </div>
          <div className={`rounded-xl border p-4 ${card}`}>
            <h2 className="font-medium">Next Product Family</h2>
            <ul className={`mt-2 space-y-1 text-sm ${muted}`}>
              {dashboard.extension_points.map((ep) => (
                <li key={ep.dev_id}><span className="font-mono text-violet-500">{ep.dev_id}</span> — {ep.name}: {ep.description}</li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {tab === 'modules' && (
        <div className={`overflow-x-auto rounded-xl border ${card}`}>
          <table className="min-w-full text-sm">
            <thead className={dark ? 'bg-slate-800' : 'bg-slate-50'}>
              <tr><th className="px-4 py-2 text-left">Module</th><th className="px-4 py-2 text-left">DEV</th><th className="px-4 py-2 text-left">Status</th><th className="px-4 py-2 text-right">Score</th><th className="px-4 py-2 text-left">Compliance</th><th className="px-4 py-2 text-left">Findings</th></tr>
            </thead>
            <tbody>
              {modules.map((m) => (
                <tr key={m.dev_id} className="border-t border-slate-800/20">
                  <td className="px-4 py-2"><p className="font-medium">{m.name}</p><p className={`text-xs ${muted}`}>{m.route}</p></td>
                  <td className="px-4 py-2 font-mono text-xs text-violet-500">{m.dev_id}</td>
                  <td className={`px-4 py-2 capitalize ${statusColor(m.status, dark)}`}>{m.status}</td>
                  <td className="px-4 py-2 text-right font-semibold">{m.score.toFixed(1)}</td>
                  <td className={`px-4 py-2 text-xs ${muted}`}>{[m.apis_ok && 'API', m.events_ok && 'Events', m.tests_ok && 'Tests', m.extension_points_ok && 'Ext'].filter(Boolean).join(' · ')}</td>
                  <td className={`px-4 py-2 text-xs ${muted}`}>{m.findings?.[0] ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'scores' && scores && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {scoreEntries.map(([key, val]) => (
            <div key={key} className={`rounded-xl border p-4 ${card}`}>
              <p className={`text-xs uppercase ${muted}`}>{key.replace(/_/g, ' ')}</p>
              <p className="mt-1 text-2xl font-semibold">{(val as number).toFixed(1)}</p>
            </div>
          ))}
          <div className={`rounded-xl border p-4 sm:col-span-2 lg:col-span-3 ${dark ? 'border-violet-800 bg-violet-950/30' : 'border-violet-200 bg-violet-50'}`}>
            <p className="text-xs uppercase text-violet-500">Overall Grade</p>
            <p className="mt-1 text-4xl font-bold text-violet-600">{scores.overall_grade.toFixed(1)}</p>
          </div>
        </div>
      )}

      {tab === 'risks' && (
        <div className="space-y-3">
          {risks.map((r) => (
            <div key={r.id} className={`rounded-xl border p-4 ${card}`}>
              <div className="flex flex-wrap items-center gap-2">
                <span className={`rounded px-2 py-0.5 text-xs uppercase ${statusColor(r.severity === 'critical' ? 'failed' : r.severity === 'medium' ? 'warning' : 'passed', dark)}`}>{r.severity}</span>
                <span className={`text-xs ${muted}`}>{r.category} · {r.module}</span>
                <span className={`ml-auto text-xs ${muted}`}>{r.status}</span>
              </div>
              <p className="mt-2 text-sm">{r.description}</p>
              {r.mitigation && <p className={`mt-1 text-xs ${muted}`}>Mitigation: {r.mitigation}</p>}
            </div>
          ))}
        </div>
      )}

      {tab === 'debt' && (
        <div className="space-y-3">
          {debt.map((d) => (
            <div key={d.id} className={`rounded-xl border p-4 ${card}`}>
              <div className="flex items-center justify-between">
                <span className="font-medium">{d.module}</span>
                <span className={`text-sm ${muted}`}>Score: {d.score.toFixed(1)}</span>
              </div>
              <p className={`mt-2 text-sm ${muted}`}>{d.description}</p>
              <p className={`mt-1 text-xs ${muted}`}>Impact: {d.impact} · Effort: {d.effort}</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'recommendations' && (
        <div className="space-y-3">
          {recommendations.map((r) => (
            <div key={r.id} className={`rounded-xl border p-4 ${card}`}>
              <div className="flex items-center gap-2">
                <span className={`rounded px-2 py-0.5 text-xs uppercase ${r.priority === 'high' ? 'bg-red-500/20 text-red-500' : r.priority === 'medium' ? 'bg-amber-500/20 text-amber-500' : 'bg-slate-500/20 text-slate-500'}`}>{r.priority}</span>
                <span className={`text-xs ${muted}`}>{r.area}{r.module ? ` · ${r.module}` : ''}</span>
              </div>
              <p className="mt-2 text-sm">{r.text}</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'status' && status && (
        <div className={`rounded-xl border p-6 ${card}`}>
          <h2 className="text-lg font-semibold">Certification Decision</h2>
          <dl className={`mt-4 grid gap-4 sm:grid-cols-2 text-sm`}>
            <div><dt className={muted}>Decision</dt><dd className={`mt-1 font-medium capitalize ${statusColor(status.decision, dark)}`}>{status.decision.replace(/_/g, ' ')}</dd></div>
            <div><dt className={muted}>Level</dt><dd className="mt-1 font-medium capitalize">{status.level.replace(/_/g, ' ')}</dd></div>
            <div><dt className={muted}>Go / No-Go</dt><dd className="mt-1 font-semibold">{status.go_no_go}</dd></div>
            <div><dt className={muted}>Overall Grade</dt><dd className="mt-1 font-semibold">{status.overall_grade.toFixed(1)}</dd></div>
            <div><dt className={muted}>Production Ready</dt><dd className="mt-1">{status.production_ready ? 'Yes' : 'No'}</dd></div>
            <div><dt className={muted}>Commercial Ready</dt><dd className="mt-1">{status.commercial_ready ? 'Yes' : 'No'}</dd></div>
            <div><dt className={muted}>Platform Complete</dt><dd className="mt-1">{status.platform_complete ? 'Yes' : 'No'}</dd></div>
            <div><dt className={muted}>Feature Frozen</dt><dd className="mt-1">{status.feature_frozen ? 'Yes' : 'No'}</dd></div>
          </dl>
          {status.production_ready && (
            <div className={`mt-6 rounded-lg border px-4 py-3 ${dark ? 'border-emerald-900 bg-emerald-950 text-emerald-300' : 'border-emerald-200 bg-emerald-50 text-emerald-800'}`}>
              Enterprise Foundation Platform V2.0 is certified and ready for enterprise product families.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
